import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import PitchDetailModal from "@/hq/campaigns/PitchDetailModal";
import { asText } from "@/hq/asText";
import { CheckCircle2, MousePointerClick, Mail } from "lucide-react";

/**
 * Phase 3I.4Q.3 — Client Profile → Pitch & Campaign History.
 *
 * Reads `/control/pitches?business_id=…` (canonical). Never overwrites
 * historical sends. Follows the same non-downgrade engagement hierarchy
 * as Q.2.
 */

const STATE_TONE = {
  INTERESTED: { fg: "#0F7B3E", bg: "#EBFBF2", bd: "#B7EBCF" },
  CLICKED:    { fg: "#8A2E00", bg: "#FFF7EE", bd: "#FCE1C7" },
  OPENED:     { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  SENT:       { fg: "#4A4A4A", bg: "#F6F5F2", bd: "#EAEAEA" },
  FAILED:     { fg: "#C1350B", bg: "#FFECEC", bd: "#F5C4C4" },
  SKIPPED:    { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
  PENDING:    { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function ClientPitchHistory({ businessId }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setItems(null);
    hqApi
      .get("/control/pitches", { params: { business_id: businessId, page_size: 50 } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => setItems([]));
  };

  useEffect(() => {
    if (businessId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  return (
    <Card className="p-6" data-testid="hq-client-pitch-history">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Pitch &amp; Campaign History</div>
          <p className="text-sm text-[#4A4A4A] mt-1">Every pitch or campaign sent to this client — historical sends are never overwritten.</p>
        </div>
        <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-[#8A8A8A]"
             data-testid="hq-client-pitch-history-total">{total} pitches</div>
      </div>

      {items === null ? (
        <div className="mt-4"><Skeleton h={140}/></div>
      ) : items.length === 0 ? (
        <div className="mt-4"><EmptyState title="No pitches sent yet" hint="Send a pitch from this client's profile to start tracking." testId="hq-client-pitch-history-empty"/></div>
      ) : (
        <ul className="mt-4 divide-y divide-[#EAEAEA]" data-testid="hq-client-pitch-history-list">
          {items.map((r) => {
            const tone = STATE_TONE[r.engagement] || STATE_TONE.PENDING;
            return (
              <li key={r.id}
                  onClick={() => setSelected(r.id)}
                  data-testid={`hq-client-pitch-history-row-${r.id}`}
                  className="py-3 cursor-pointer hover:bg-[#FFF7EE] px-2 -mx-2 rounded-lg">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-black text-[14px] uppercase tracking-[0.14em] text-[#FF6A00]">
                    {r.pitch_type === "full" ? "Full Pitch" : r.pitch_type || "—"}
                  </div>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.06em] uppercase"
                    style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
                    data-testid={`hq-client-pitch-history-state-${r.id}`}
                  >{r.engagement}</span>
                </div>
                <div className="mt-1 text-[13px] text-[#4A4A4A] truncate">{asText(r.subject) || "—"}</div>
                <div className="mt-1 flex flex-wrap gap-4 text-[11px] text-[#8A8A8A]">
                  <span>Sent {fmt(r.sent_at)}</span>
                  <span className="inline-flex items-center gap-1"><Mail size={11}/> Open ×{r.open_count || 0}</span>
                  <span className="inline-flex items-center gap-1"><MousePointerClick size={11}/> Click ×{r.click_count || 0}</span>
                  {r.interested && <span className="inline-flex items-center gap-1 text-[#0F7B3E]"><CheckCircle2 size={11}/> Interested {fmt(r.interested_at)}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <PitchDetailModal
        commId={selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </Card>
  );
}
